<script setup>
//PlaceholdersDynamic.vue
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const { t } = useI18n();

const props = defineProps({
  columnOptions: {
    type: Array,
    required: true,
    default: () => [],
  },
  selectedTemplate: {
    type: Object,
    required: true,
    default: () => ({ components: [] }),
  },
  initialPlaceholders: {
    type: Object,
    default: () => ({}),
  },
});

// Adicione um novo emit
const emit = defineEmits(['submit', 'change']);


const extractPlaceholders = (text) => {
  const regex = /{{([^{}]+)}}/g;
  const matches = text?.match(regex) || [];
  return matches.map((match) => match.replace(/{{|}}/g, '').trim());
};

const placeholders = computed(() => {
  const bodyComponent = props.selectedTemplate?.components?.find(
    (component) => component.type === 'BODY'
  );
  return bodyComponent ? extractPlaceholders(bodyComponent.text) : [];
});

const placeholderPairs = computed(() => {
  const pairs = [];
  for (let i = 0; i < placeholders.value.length; i += 2) {
    pairs.push(placeholders.value.slice(i, i + 2));
  }
  return pairs;
});

const placeholderSelections = ref({});

const initializeSelections = () => {
  placeholderSelections.value = {};
  placeholders.value.forEach((placeholder) => {
    placeholderSelections.value[placeholder] = props.initialPlaceholders[placeholder] || '';
  });
};

initializeSelections();

// Watch for changes in initialPlaceholders
watch(
  () => props.initialPlaceholders,
  (newInitialPlaceholders) => {
    if (newInitialPlaceholders && Object.keys(newInitialPlaceholders).length > 0) {
      placeholders.value.forEach((placeholder) => {
        if (newInitialPlaceholders[placeholder]) {
          placeholderSelections.value[placeholder] = newInitialPlaceholders[placeholder];
        }
      });
      v$.value.$reset();
    }
  },
  { deep: true }
);

// Watch for changes in columnOptions and placeholders
watch(
  [() => props.columnOptions, placeholders],
  ([newColumnOptions, newPlaceholders]) => {
    const oldSelections = { ...placeholderSelections.value };
    initializeSelections();
    newPlaceholders.forEach((placeholder) => {
      if (oldSelections[placeholder] && newColumnOptions.some(opt => opt.value === oldSelections[placeholder])) {
        placeholderSelections.value[placeholder] = oldSelections[placeholder];
      }
    });
    v$.value.$reset();
  },
  { deep: true }
);


const rules = computed(() => {
  const validationRules = {};
  placeholders.value.forEach((placeholder) => {
    validationRules[placeholder] = { required };
  });
  return validationRules;
});

const v$ = useVuelidate(rules, placeholderSelections);

const formErrors = computed(() => {
  const errors = {};
  placeholders.value.forEach((placeholder) => {
    errors[placeholder] = v$.value[placeholder].$error
      ? t('WHATSAPP_TEMPLATES.MAPPING_PLACEHOLDERS.ERROR') + placeholder
      : '';
  });
  return errors;
});

const onColumnChange = (placeholder, newColumn) => {



  placeholderSelections.value[placeholder] = newColumn;
  v$.value[placeholder].$touch();
  // ✅ Emitir evento de mudança
  emit('change', placeholderSelections.value);
};



const validateAndEmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return false;
  }
  emit('submit', placeholderSelections.value);
  return true;
};

defineExpose({ validateAndEmit, placeholderSelections });

const formatLabel = (placeholder) => {
  const placeholderText = `<span class="embossed-placeholder">${placeholder}</span>`;
  const translated = t('WHATSAPP_TEMPLATES.MAPPING_PLACEHOLDERS.COLUMN_LABEL', { placeholder: placeholderText });
  return translated || `Coluna para <strong>${placeholderText}</strong>`;
};
</script>

<template>
  <div class="space-y-6">
    <div v-if="placeholders.length === 0" class="text-center p-8">
      <div class="text-n-slate-10 dark:text-n-slate-11 text-sm bg-n-slate-1 dark:bg-n-slate-8 rounded-lg p-6">
        <span class="text-2xl mb-2 block">📝</span>
        {{ t('WHATSAPP_TEMPLATES.MAPPING_PLACEHOLDERS.NO_PLACEHOLDERS') }}
      </div>
    </div>

    <div v-else class="space-y-6">
      <div class="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
        <div class="flex items-start gap-3">
          <span class="text-lg">💡</span>
          <div>
            <strong>Dica:</strong> Mapeie cada placeholder do template para uma coluna da sua tabela de dados.
            <div class="mt-2 text-xs text-green-600 dark:text-green-400">
              Os placeholders são as partes do texto que serão substituídas pelos dados reais de cada contato.
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <div
          v-for="(pair, index) in placeholderPairs"
          :key="index"
          class="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div
            v-for="placeholder in pair"
            :key="placeholder"
            class="flex flex-col gap-2"
          >
            <label
              :for="`column-${placeholder}`"
              class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2"
              v-html="`<span class='text-green-600 dark:text-green-400'>🔗</span> ${formatLabel(placeholder)}`"
            />
            <ComboBox
              :id="`column-${placeholder}`"
              v-model="placeholderSelections[placeholder]"
              :options="props.columnOptions"
              :placeholder="t('WHATSAPP_TEMPLATES.MAPPING_PLACEHOLDERS.COLUMN_PLACEHOLDER')"
              class="combobox"
              :message="formErrors[placeholder]"
              :has-error="!!formErrors[placeholder]"
              @update:modelValue="onColumnChange(placeholder, $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.combobox {
  & > div > button {
    @apply bg-white dark:bg-slate-800 border-n-slate-4 dark:border-n-slate-7 hover:border-green-300 dark:hover:border-green-600 focus:border-green-500 dark:focus:border-green-500 focus:ring-green-200 dark:focus:ring-green-800;
  }
  & > div > button:not(.focused) {
    @apply hover:border-green-300 dark:hover:border-green-600;
  }
}

:deep(.embossed-placeholder) {
  @apply px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs font-mono border border-green-200 dark:border-green-800;
}
</style>
